import { useMemo } from "react";
import { Label } from "../ui/label";
import { SopBundleBar } from "./SopBundleBar";
import { StandardMultiSelect } from "./StandardMultiSelect";
import { StandardReqChecklist } from "./StandardReqChecklist";
import { RelatedSuggestions } from "./RelatedSuggestions";

// selection: { [requirementId]: string[] partLabels } — [] means the whole requirement
// applicableIds (optional Set): requirements that apply to the entity's registered functions —
// adding a standard then auto-selects only those, and others are flagged.
export const CrossStandardPicker = ({ reqs, standards, stdIds, setStdIds, selection, setSelection, apiBase = "/sop", applicableIds = null }) => {
  const byStd = useMemo(() => {
    const m = {};
    reqs.forEach((r) => { if (!/^reserved/i.test((r.text || "").trim())) (m[r.standard_id] = m[r.standard_id] || []).push(r); });
    return m;
  }, [reqs]);
  const reqById = useMemo(() => Object.fromEntries(reqs.map((r) => [r.id, r])), [reqs]);

  const withStd = (ids) => setStdIds((prev) => Array.from(new Set([...prev, ...ids])));
  const toggleStd = (sid) => {
    if (stdIds.includes(sid)) return removeStd(sid);
    withStd([sid]);
    if (applicableIds) {
      setSelection((p) => {
        const n = { ...p };
        (byStd[sid] || []).forEach((r) => { if (applicableIds.has(r.id)) n[r.id] = n[r.id] || []; });
        return n;
      });
    }
  };
  const removeStd = (sid) => {
    setStdIds((p) => p.filter((x) => x !== sid));
    setSelection((p) => Object.fromEntries(Object.entries(p).filter(([id]) => reqById[id]?.standard_id !== sid)));
  };
  const toggleReq = (id) => setSelection((p) => {
    const n = { ...p };
    if (n[id] !== undefined) delete n[id]; else n[id] = [];
    return n;
  });
  const togglePart = (id, label) => setSelection((p) => {
    const cur = p[id] || [];
    return { ...p, [id]: cur.includes(label) ? cur.filter((x) => x !== label) : [...cur, label] };
  });
  const setAll = (sid, on) => setSelection((p) => {
    const n = { ...p };
    (byStd[sid] || []).forEach((r) => { if (on) n[r.id] = n[r.id] || []; else delete n[r.id]; });
    return n;
  });
  const applyBundle = (ids, parts, standardIds) => {
    const sel = {};
    ids.filter((id) => reqById[id]).forEach((id) => { sel[id] = parts[id] || []; });
    setSelection(sel);
    setStdIds(Array.from(new Set([...(standardIds || []), ...Object.keys(sel).map((id) => reqById[id].standard_id)])));
  };
  const addSuggestion = (s) => {
    withStd([s.standard_id]);
    setSelection((p) => ({ ...p, [s.requirement_id]: p[s.requirement_id] || [] }));
  };

  const total = Object.keys(selection).length;
  return (
    <div className="space-y-3" data-testid="sop-cross-standard-picker">
      <SopBundleBar selection={selection} onApply={applyBundle} apiBase={apiBase} />
      <div>
        <Label>Standards</Label>
        <StandardMultiSelect standards={standards} value={stdIds} onToggle={toggleStd} />
      </div>
      {stdIds.map((sid) => (
        <StandardReqChecklist key={sid} standardId={sid} reqs={byStd[sid] || []} selection={selection}
          onToggleReq={toggleReq} onTogglePart={togglePart} onSetAll={setAll} onRemove={removeStd} applicableIds={applicableIds} />
      ))}
      <div className="text-[11px] text-muted-foreground" data-testid="sop-selected-count">
        {total} requirement{total === 1 ? "" : "s"} selected across {new Set(Object.keys(selection).map((id) => reqById[id]?.standard_id)).size} standard(s)
      </div>
      <RelatedSuggestions selection={selection} onAdd={addSuggestion} apiBase={apiBase} />
    </div>
  );
};
